import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { MessageSquare, Lightbulb, Bug } from "lucide-react";
import { toast } from "sonner";

export const FeedbackDialog = ({ open, onOpenChange, type = 'general' }) => {
  const { user, userProfile, company } = useAuth();
  const [feedback, setFeedback] = useState('');
  const [title, setTitle] = useState('');
  const [priority, setPriority] = useState('medium');
  const [category, setCategory] = useState('');
  const [stepsToReproduce, setStepsToReproduce] = useState('');
  const [expectedBehavior, setExpectedBehavior] = useState('');
  const [actualBehavior, setActualBehavior] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const resetForm = () => {
    setFeedback('');
    setTitle('');
    setPriority('medium');
    setCategory('');
    setStepsToReproduce('');
    setExpectedBehavior('');
    setActualBehavior('');
  };

  const handleSubmit = async () => {
    // Validation based on type
    if (type === 'feature' && !title.trim()) {
      toast.error('Please enter a feature title');
      return;
    }

    if (type === 'bug' && !title.trim()) {
      toast.error('Please enter a bug title');
      return;
    }

    if (!feedback.trim()) {
      toast.error(type === 'general' ? 'Please enter your feedback' : 'Please enter a description');
      return;
    }

    setIsSubmitting(true);

    try {
      // Build the content based on type
      let fullContent = feedback;

      if (type === 'feature') {
        fullContent = `**Title:** ${title}\n\n**Priority:** ${priority}\n\n**Category:** ${category || 'Not specified'}\n\n**Description:**\n${feedback}`;
      } else if (type === 'bug') {
        fullContent = `**Title:** ${title}\n\n**Priority:** ${priority}\n\n**Steps to Reproduce:**\n${stepsToReproduce}\n\n**Expected Behavior:**\n${expectedBehavior}\n\n**Actual Behavior:**\n${actualBehavior}\n\n**Additional Details:**\n${feedback}`;
      }

      const { error } = await supabase
        .from('feedback')
        .insert({
          user_id: userProfile?.id || null,
          company_id: company?.id || null,
          type: type,
          content: fullContent,
          user_email: user?.email || null,
          user_name: userProfile?.first_name && userProfile?.last_name
            ? `${userProfile.first_name} ${userProfile.last_name}`
            : userProfile?.first_name || userProfile?.last_name || user?.email?.split('@')[0] || 'Anonymous',
          status: 'new',
          priority: type !== 'general' ? priority : null
        });

      if (error) {
        console.error('Error submitting feedback:', error);
        toast.error('Failed to submit feedback. Please try again.');
      } else {
        const successMessage = type === 'feature'
          ? 'Feature request submitted successfully! Thank you.'
          : type === 'bug'
          ? 'Bug report submitted successfully! We\'ll look into it.'
          : 'Feedback submitted successfully! Thank you.';
        toast.success(successMessage);
        resetForm();
        onOpenChange(false);
      }
    } catch (error) {
      console.error('Error in handleSubmit:', error);
      toast.error('An unexpected error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getDialogConfig = () => {
    switch (type) {
      case 'feature':
        return {
          title: 'Request a Feature',
          description: 'Have an idea for a new feature? We\'d love to hear it!',
          icon: <Lightbulb className="h-5 w-5 text-blue-500" />,
        };
      case 'bug':
        return {
          title: 'Report a Bug',
          description: 'Found something that isn\'t working? Let us know so we can fix it.',
          icon: <Bug className="h-5 w-5 text-red-500" />,
        };
      default:
        return {
          title: 'General Feedback',
          description: 'Share your thoughts, suggestions, or questions with us.',
          icon: <MessageSquare className="h-5 w-5 text-green-500" />,
        };
    }
  };

  const config = getDialogConfig();

  const renderFormFields = () => {
    if (type === 'feature') {
      return (
        <>
          <div className="space-y-2">
            <Label htmlFor="title">Feature Title *</Label>
            <Input
              id="title"
              placeholder="Brief title for your feature request"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="priority">Priority</Label>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger id="priority">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="category">Category (Optional)</Label>
              <Input
                id="category"
                placeholder="e.g., UI, Performance"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="feedback">Description *</Label>
            <Textarea
              id="feedback"
              placeholder="Describe the feature and why it would be valuable..."
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              rows={5}
              className="resize-none"
            />
          </div>
        </>
      );
    }

    if (type === 'bug') {
      return (
        <>
          <div className="space-y-2">
            <Label htmlFor="title">Bug Title *</Label>
            <Input
              id="title"
              placeholder="Brief description of the bug"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="priority">Priority</Label>
            <Select value={priority} onValueChange={setPriority}>
              <SelectTrigger id="priority">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="stepsToReproduce">Steps to Reproduce</Label>
            <Textarea
              id="stepsToReproduce"
              placeholder="1. Go to...&#10;2. Click on...&#10;3. See error..."
              value={stepsToReproduce}
              onChange={(e) => setStepsToReproduce(e.target.value)}
              rows={3}
              className="resize-none"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="expectedBehavior">Expected Behavior</Label>
            <Textarea
              id="expectedBehavior"
              placeholder="What should happen..."
              value={expectedBehavior}
              onChange={(e) => setExpectedBehavior(e.target.value)}
              rows={2}
              className="resize-none"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="actualBehavior">Actual Behavior</Label>
            <Textarea
              id="actualBehavior"
              placeholder="What actually happens..."
              value={actualBehavior}
              onChange={(e) => setActualBehavior(e.target.value)}
              rows={2}
              className="resize-none"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="feedback">Additional Details *</Label>
            <Textarea
              id="feedback"
              placeholder="Any other relevant information..."
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              rows={3}
              className="resize-none"
            />
          </div>
        </>
      );
    }

    // General feedback
    return (
      <div className="space-y-2">
        <Label htmlFor="feedback">Your Feedback</Label>
        <Textarea
          id="feedback"
          placeholder="Share your thoughts, suggestions, or questions..."
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          rows={6}
          className="resize-none"
        />
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={(open) => {
      if (!open) resetForm();
      onOpenChange(open);
    }}>
      <DialogContent className="sm:max-w-[550px] max-h-[90vh] overflow-hidden p-0">
        <div className="overflow-y-auto max-h-[90vh] p-6">
        <DialogHeader>
          <div className="flex items-center gap-2">
            {config.icon}
            <DialogTitle>{config.title}</DialogTitle>
          </div>
          <DialogDescription>
            {config.description}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {renderFormFields()}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              resetForm();
              onOpenChange(false);
            }}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || !feedback.trim() || (type !== 'general' && !title.trim())}
          >
            {isSubmitting ? 'Submitting...' : type === 'feature' ? 'Submit Request' : type === 'bug' ? 'Submit Report' : 'Submit Feedback'}
          </Button>
        </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};
